import { Self, Type } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  HasColloquial,
  HasEDPIngestionMethod,
  HasGetReadPath,
  HasRelationships,
  HasRuleAssociations,
} from './column-composition-fns';
import {
  CellRenderer,
  ControlListenerPlugin,
  ControlValidatorPlugin,
  FormControlMaker,
  FormControlOptions,
  FormRegisterableComponent,
  FormRegisterableComponentPlugin,
} from './control-types';
import { Data } from './data';
import { HasValidatorFn } from './has-validator-fn';
import { Section } from './section';

/**
 * Interface that reflects the required fields
 * structure gotten from the backend.
 */
export interface ActionFields {
  // this can be the field to get the record data
  // or the value itself.
  [backEndFieldName: string]: string | any;
}

export interface Action {
  method: string;
  fields: ActionFields;
}

export enum ActionTypes {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

export interface Parent {
  entity: string;
  idField?: any;
  update?: Action;
  create?: Action;
  delete?: Action;
  read?: string;
}

export interface RelatedFieldOptions {
  columnId: string;
  relationship: string;
}

export interface FieldValidation {
  type: string;
  format: string;
  value: string;
}

/**
 * Rule association object
 */
export interface RuleAssociation {
  rule_id: number;
  rule_name: string;
}

export interface ColumnOptions {
  validations?: string[];
  fieldValidations?: FieldValidation[];
  format?: string;
  relatedFields?: RelatedFieldOptions[];
  rules?: RuleAssociation[];
  sortable?: boolean;
}

export enum DataType {
  Id = 'id',
  Boolean = 'boolean',
  String = 'string',
  Timestamp = 'timestamp',
  TimestampWithTimeZone = 'timestamp with time zone',
  Text = 'text',
  Month = 'month',
  TextArea = 'textarea',
  Integer = 'integer',
  Numeric = 'numeric',
  Int = 'int',
  Date = 'date',
}

/**
 * Valid Catalog Input Types
 */
export enum InputType {
  /*
   * DB CATALOG SUPPORTED TYPES
   */

  /**
   * Text input type
   */
  Text = 'Text',
  /**
   * Dropdown input type
   */
  Dropdown = 'Dropdown',
  /**
   * Slider input type
   */
  Slider = 'Slider',
  /**
   * Number input type
   */
  Number = 'Number',
  /**
   * Date input type
   */
  Date = 'Date',
  /**
   * MaxValue input type
   */
  MaxValue = 'MaxValue',
  /**
   * MinValue input type
   */
  MinValue = 'MinValue',
  /**
   * NAICS input type
   */
  NAICS = 'NAICS',
  /**
   * Array input type
   */
  Array = 'Array',
  /**
   * TextArea input type
   */
  TextArea = 'TextArea',

  /**
   * Year selector input type
   */
  Year = 'Year',
  /*
   * UNSUPPORTED INPUT TYPES (NOT IN DB BUT ACCEPTED)
   */
  /**
   * Chips input type - Valid but catalog not configurable on catalog DB
   */
  Chips = 'Chips',
  /**
   * StrictChips input type - Valid but catalog not configurable on catalog DB
   */
  StrictChips = 'StrictChips',
  /**
   * Checkbox input type - Valid but catalog not configurable on catalog DB
   */
  Checkbox = 'Checkbox',
  /**
   * Image input type - Valid but catalog not configurable on catalog DB
   */
  Image = 'Image',
}

export enum FilterType {
  Range = 'Range',
  Equal = 'Equal',
  Multi = 'Multi',
  Industry = 'Industry Code',
  EqualNoCase = 'EqualNoCase',
}

export interface ColumnListener {
  /**
   * the column change to listen
   */
  targetColumnId: number;
  /**
   * function that return partial changes to a column definition, ideally
   * should be used to modify behaviors (required, isDisabled, header, max, min,
   * etc) but not limited to change any Column attribute
   */
  columnBehaviorModifier?: (change: RawColumn) => Partial<RawColumn>;
  /**
   * this will override the current value with null if not provided. if
   * provided will replace with anything the function returns, the function
   * receives two parameters, first is the publisher change, second is current
   * column state(value)
   */
  valueSetter?: (change: RawColumn, self: RawColumn) => any;
  /**
   * if self change should replicate an event change. default should be false
   */
  proxy?: boolean;
  /**
   * To re-fetch column LOVs on publisher change default is true,
   * if false the LOV re-fetch will be ignored on change streams (but not the initial one)
   */
  fetchOnChange?: boolean;
}

export interface RawColumn {
  /**
   * record field
   */
  field: string;
  /**
   * Filter  data type (used in backend to create ES queries)
   */
  dataType: string | DataType;
  /**
   * column header to display
   */
  header: string;
  /**
   * The column id
   */
  columnId?: number;
  /**
   * Filter input type (used in backend to create ES queries)
   */
  inputType?: string | InputType;
  /**
   * Filter type (used in backend to create ES queries)
   */
  type?: string | FilterType;
  /**
   * Alternative field path with jsonata format
   */
  path?: string;
  /**
   * LOB url
   */
  lookupCategory?: string;
  /**
   * denotes if column tooltip icon is shown or not
   */
  showTooltipIcon?: boolean;
  /**
   * AgGrid & column tooltip
   */
  tooltip?: string | Function;
  /**
   * denotes column is required in specific context (edp, form, clone-able column)
   */
  required?: boolean;
  /**
   * EDP column parent configuration
   */
  parent?: Parent;
  /**
   * Validation options
   */
  options?: ColumnOptions;
  /**
   * Target attribute for record field value
   */
  value?: any;
  /**
   * Target field for LOB results
   */
  values?: any[];
  /**
   * @deprecated in favor of Component
   * Custom TemplateRef name for column
   */
  templateRefName?: string;
  /**
   * on number inputs used to indicate the max value allowed
   */
  max?: number;
  /**
   * on number inputs used to indicate the min value allowed
   */
  min?: number;
  /**
   * step for numbers to increase on number inputs
   */
  step?: number;
  /**
   * @deprecated
   * indicates if column is part of a new record
   */
  isNew?: boolean;
  /**
   * @deprecated
   * index in array
   */
  arrayIndex?: number;
  /**
   * @deprecated
   * index in array
   * TODO: remove arrayIndex
   */
  index?: number;
  /**
   * @deprecated
   * id value for record
   */
  id?: number | string;
  /**
   * @deprecated not used
   * Special attribute to denote a column has invalid UOM selection
   * TODO: rename to `invalid` and refactor any implementation
   */
  UOMValidation?: boolean;
  /**
   * Indicates a column should be disabled
   */
  isDisabled?: boolean;
  /**
   * Tooltip to display when column is disabled
   */
  disabledTooltip?: string;
  /**
   * Indicates a column should be hidden
   */
  hidden?: boolean;
  /**
   * @deprecated
   * if true, will emit events through event bus (Broadcaster in record details)
   */
  publisher?: boolean;
  /**
   * @deprecated
   * if provided, used in broadcaster to emit an event
   */
  publisherAction?: string;
  /**
   * @deprecated
   * if present will subscribe to event bus listening emitted events through
   * event bus (Broadcaster)
   */
  subscribeOn?: ColumnListener;
  /**
   * modifies the attribute name where to look the LOV value
   */
  lookupValueKey?: string;
  /**
   * @deprecated
   * Column condition depending on MIQQL parser to convert to a function
   */
  condition?: string;
  /**
   * Column flagged as favorite
   */
  pinned?: boolean;
  /**
   * To flag a Column as sortable in tables
   */
  sortable?: boolean;
  /**
   * To flag a Column as filterable in tables
   */
  filter?: boolean;
  /**
   * To flag a Column as resizable in tables
   */
  resizable?: boolean;
  /**
   * If set as true, allows the user to press the '-' key
   */
  allowNegatives?: boolean;
  /**
   * To highlight date component when there is any warning
   */
  warning?: boolean;
  /**
   * Adapter function to override the way a column is displayed
   * to the end user
   */
  displayFn?<T = any>(data?: T, ...args: any): string | Data;
  /**
   * Adapter function to override a value to final record/column value
   */
  valueSetter?<T = any>(data?: T): any;
  showInvalidLookupValue?: boolean;
}

export interface ControlValidityRuleContext {
  data: Data;
  column: Column;
}

export type ControlValidityRule = (
  control: AbstractControl,
  context: ControlValidityRuleContext
) => { [key: string]: any } | null;

interface CompositedColumn
  extends HasColloquial, HasGetReadPath, HasRelationships, HasValidatorFn, HasRuleAssociations, HasEDPIngestionMethod {}

export enum ColumnInputMessageType {
  Default = 'default',
  Error = 'error',
  Warning = 'warning',
}
export interface ColumnInputMessage {
  message: string;
  type?: ColumnInputMessageType;
  show?: boolean;
  params?: Data;
}

/**
 * Base Column interface
 */
export interface Column extends CompositedColumn, RawColumn {
  /**
   * associated Column
   */
  unit?: this;
  /**
   * All Associated Column list
   */
  related?: this[];
  /**
   * in case this is an association, it describes the type of relation it is representing
   * so they can be filterable/categorizable
   */
  relationType?: string;
  /**
   * aux value to identify uom fields
   */
  uomId?: number;
  /**
   * Section reference
   */
  section?: Section<this>;
  /**
   * column placeholder to display
   */
  placeholder?: string;
  /**
   * @deprecated use FormControlOptions.validatorFn
   * Business rules to apply as form Validator
   */
  validityRules?: ControlValidityRule[];
  /**
   * FormControlOptions
   */
  formControlOptions?: FormControlOptions<Column>;
  /**
   * FormGroupComponent<Column>
   */
  Component?: Type<FormRegisterableComponent<Column>>;
  /**
   * Used to store properties that are required by another component
   */
  componentConfig?: Data;
  /**
   * Configured set of icons and handlers
   */
  inputIcons?: { icon: string; handle: (...args: any) => void; tooltip?: string; tooltipTranslation?: string }[];
  /**
   * Link Generator
   */
  linkGenerator?: (data: Data) => string[];
  /**
   * Cell renderer component type CellRenderer<Self>
   */
  CellRenderer?: Type<CellRenderer<Self>>;
  /**
   * Cell renderer to be used to configure it
   */
  CellRendererConfig?: Data;
  /**
   * Used to disable dates in date-picker
   */
  minDate?: Date;
  /**
   * Messages that the column should show just below the input
   * these can be configured by a component, a plugin or in the columns-override.config
   */
  inputMessages?: ColumnInputMessage[];

  /**
   * Force onBlur event even if prev and next are the same or null
   */
  alwaysEmitChanges?: boolean;

  /**
   * Only for date input fields, when "true" will disabled direct date manipulation & will open datepicker box instead.
   */
  readonly?: boolean;
  /**
   * This service is used to create and do control.setValidator to given tuples of control/config
   */
  validatorPlugin?: Type<ControlValidatorPlugin<any>>;
  /**
   * This service is used to create and do control.setListener to given tuples of control/config
   */
  warningValidatorPlugin?: Type<ControlValidatorPlugin<any>>;

  /**
   * validatorPlugin configs
   */
  validatorPluginConfig?: any;
  /**
   * This listener is used to set custom Listener
   */
  controlListener?: Type<ControlListenerPlugin<any>>;
  /**
   * This service will replace the configured in the component to build/find his
   * targeted control in the main form
   */
  controlMaker?: Type<FormControlMaker<any>>;
  /**
   * Service to add behavior to given component
   */
  ComponentPlugin?: {
    Type: Type<FormRegisterableComponentPlugin<Column>>;
    options?: Data;
  };
  /**
   *  Specify the list of usages that the sections wil be shown
   */
  usageTarget?: string[];
  /**
   * Default LOV payload use when getting Column's LOVs
   */
  lovDefaultPayload?: any;
  /**
   * Adding Custom column configuration
   */
  customConfig?: Data;
  /**
   * To flag a Column as allowed for alias creation
   */
  isAliasColumn?: boolean;
  /**
   * Tooltip to display when the column is disabled
   */
  disabledTooltip?: string;
}
